import { AfterViewChecked, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';

import { AufnahmePunktDocument, punktLabel } from '../../data-model';
import { DataService } from '../../data.service';
import { cloneDeep } from 'lodash';
import { FilterService } from 'src/app/filter.service';
import { Command, NavbarService } from 'src/app/navbar.service';
import { AuthService } from 'src/app/auth.service';
import { ProjectService } from 'src/app/project.service';
import { BohrpunktNeuComponent } from './bohrpunkt-neu.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ValidationService } from '../validation.service';
import { updateAccordingly } from './bohrpunkt-detail.resolver';
import { MapStatusService, TransformationService, StorageService, ConfirmService } from '@luzula/luzula-lib';
import { DeleteConfirmationService } from '../../shared/delete-confirmation/delete-confirmation.service';
import { latLng, latLngBounds, LatLngBounds } from 'leaflet';

@Component({
    standalone: false,
	selector: 'bp-bohrpunkt',
	templateUrl: './bohrpunkt.component.html',
	styleUrls: ['./bohrpunkt.component.css'],
	
})
export class BohrpunktComponent implements OnInit, OnDestroy, AfterViewChecked {
	private modalService = inject(NgbModal);

	private destroy$ = new Subject<void>();

	bps: any[] = [];
	pointsFilter: boolean[] = [];
	boundsFilter: boolean[] = [];
	currentBounds?: LatLngBounds;
	bp!: AufnahmePunktDocument;
	active = '';
	dirty = false;

	punktLabel = punktLabel;

	constructor(
		private aus: AuthService,
		private ps: ProjectService,
		private router: Router,
		private route: ActivatedRoute,
		private cd: ChangeDetectorRef,
		private ds: DataService,
		private fs: FilterService,
		private ns: NavbarService,
		private vs: ValidationService,
		private storage: StorageService,
		private mapStatusService: MapStatusService,
		private ts: TransformationService,
		private confirmService: ConfirmService,
		private deleteConfirmation: DeleteConfirmationService) {

		this.vs.dirty.pipe(takeUntil(this.destroy$)).subscribe((v: boolean) => this.dirty = v);
		this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
			this.initialized = false;
			this.bp = data['bp'];
			this.active = this.bp._id;

			this.initPoints();
		});
		// pointSaved nur einmal abonnieren, nicht bei jedem Routenwechsel
		this.ds.pointSaved.pipe(takeUntil(this.destroy$)).subscribe((bp: AufnahmePunktDocument) => {
			this.bps = updateAccordingly(bp, this.bps);
		});
		this.aus.authChanged.pipe(takeUntil(this.destroy$)).subscribe(() => this.initButtons());
		this.initButtons();
		const currentFilter = this.storage.getSync('currentFilter');
		if (currentFilter) {
			if (this.ds.isInitialized) {
				this.fs.applyFilter(currentFilter);
			}
			else this.ds.initialized.pipe(takeUntil(this.destroy$)).subscribe(() => this.fs.applyFilter(currentFilter));
		}
		this.mapStatusService.globalMapState().pipe(takeUntil(this.destroy$)).subscribe(state => {
			if (state.bounds) {
				const b = state.bounds as any;
				if (b._southWest && b._northEast) {
					this.currentBounds = latLngBounds(
						latLng(b._southWest.lat, b._southWest.lng),
						latLng(b._northEast.lat, b._northEast.lng)
					);
				} else if (typeof state.bounds.contains === 'function') {
					this.currentBounds = state.bounds;
				}
				this.updateBoundsFilter();
			}
		});

		this.fs.apply.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateFilter());
		this.fs.filtersUpdated.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateFilter());
	}

	private initialized = false;
	private initiallyScrollToActive() {
		if (this.initialized) return;
		const element = document.querySelector(`#id-${this.active}`);
		if (element) {
			this.initialized = true;
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	public async canDeactivate(): Promise<boolean> {
		if (!this.dirty) return true;
		const result = await this.confirmService.confirm({
			title: 'Ungespeicherte Änderungen',
			message: 'Es gibt ungespeicherte Änderungen an diesem Punkt.',
			confirmText: 'Verwerfen',
			confirmClass: 'btn-warning',
			extraText: 'Speichern & zurück',
			extraClass: 'btn-success',
			cancelText: 'Bleiben'
		});
		if (result === 'extra') {
			// Speichern und dann zurück
			await this.ds.saveDocument(this.bp, { user: this.aus.currentUser });
			this.vs.dirty.next(false);
			return true;
		}
		if (result === true) {
			// Verwerfen: Dokument aus DB neu laden
			try {
				const freshBp = await firstValueFrom(this.ds.getDocument$(this.bp._id));
				Object.assign(this.bp, this.ds.updateBohrpunkt(freshBp as any, false));
			} catch (e) { /* Fallback: trotzdem erlauben */ }
			this.vs.dirty.next(false);
			return true;
		}
		return false; // Bleiben
	}

	private initButtons() {
		// Punkt anlegen: globale Rolle prüfen
		if (this.aus.canI('add', 'points')) {
			this.ns.subscribe(Command.addPoint, (data?: any) => this.onAdd(data));
		} else {
			this.ns.unsubscribe(Command.addPoint);
		}

		// Punkt löschen: Projekt-Berechtigung prüfen (inkl. Bearbeiter-Mitgliedschaft)
		if (this.ps.canIremove()) {
			this.ns.subscribe(Command.deletePoint, () => this.onRemove());
		} else {
			this.ns.unsubscribe(Command.deletePoint);
		}

		// Export für aktuellen Punkt
		this.ns.subscribe(Command.exportPdf, () => this.onExportPdf());
	}

	async initPoints() {
		const bps = await this.ds.queryIndex();
		this.bps = [];
		bps?.rows.forEach((row: any) => {
			const mesoklima = row.key[7];
			const stfgData = row.key[8];
			const bp = { _id: row.id, type: row.key[1], header: row.key[4], state: row.key[5], version: row.key[6], extendedHeader: (mesoklima || stfgData) ? { mesoklima: mesoklima || undefined, stfg: stfgData ? { naehrkraftStufe: stfgData.nk, stammFeuchteStufe: { first: stfgData.sf1, second: stfgData.sf } } : undefined } : undefined };
			const updatedBp = this.ds.updateBohrpunkt(bp, false); // Kein WMS-Fetch für Listen-Punkte
			updatedBp._latLng = this.ts.from(updatedBp.header.koordinaten);
			this.bps.push(updatedBp);
		});
		this.updateFilter();
		this.updateBoundsFilter();
	}

	private updateFilter() {
		this.pointsFilter = [];
		const filteredBps: any[] = [];
		const notFilteredBps: any[] = [];
		this.bps.forEach((bp: any) => {
			if (this.fs.filteredDocIds.has(bp._id))
				filteredBps.push(bp);
			else
				notFilteredBps.push(bp);
		});
		this.bps = filteredBps.concat(notFilteredBps);
		this.bps.forEach((bp: any) => {
			this.pointsFilter.push(this.fs.filteredDocIds.has(bp._id));
		});
		this.updateBoundsFilter();
		this.initiallyScrollToActive();
	}

	ngOnInit() {
		this.ds.connected.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.ds.nummerChanged.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
				const idx = this.bps.findIndex((bp: any) => bp._id === value.id);
				if (idx !== -1) {
					this.bps[idx].header.nummer = value.num;
				}
				this.bps = cloneDeep(this.bps);
			});
			this.initButtons();
		});
	}

	ngAfterViewChecked(): void {
		this.initiallyScrollToActive();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.ns.unsubscribe(Command.addPoint);
		this.ns.unsubscribe(Command.deletePoint);
		this.ns.unsubscribe(Command.exportPdf);
	}

	select(e: Event, bp: AufnahmePunktDocument) {
		e.stopPropagation();
		this.router.navigate(['/bohrpunkte', bp._id]);
	}

	onAdd(data?: any) {
		const modalRef = this.modalService.open(BohrpunktNeuComponent);
		modalRef.componentInstance.position = data;
		modalRef.componentInstance.bpTemplate = this.bp;
		modalRef.closed.subscribe((value: any) => {
			const bp = value.bp;
			this.ds.createBohrpunktFrom(bp)?.then((response: any) => {
				this.bps.push(bp);
				this.initialized = false;
				this.router.navigate(['/bohrpunkte', response.id]);
			});
		});
	}

	async onRemove() {
		const confirmed = await this.deleteConfirmation.confirmDeleteSingle(this.bp);
		if (confirmed) {
			this.ds.removeBohrpunkt(this.bp._id).then((pouchdbResponse: any) => {
				if (pouchdbResponse.ok && pouchdbResponse.id) {
					this.initPoints().then(() => {
						if (this.bps.length > 0)
							this.router.navigate(['/bohrpunkte', this.bps.at(0)._id]);
						else
							this.router.navigate(['/']);
					})
				}
			});
		}
	}

	updateBoundsFilter() {
		if (!this.currentBounds || this.bps.length === 0) return;
		this.boundsFilter = this.bps.map(bp => bp._latLng ? this.currentBounds!.contains(bp._latLng) : true);
	}

	private onExportPdf() {
		if (!this.bp?._id) return;
		this.router.navigate(['/bohrpunkte/Drucken/multi'], { queryParams: { ids: this.bp._id } });
	}
}
